const PROTOCOL = 'codersports.scratch.controller/v1';
const ALLOWED_PARENT_ORIGINS = new Set([
    'https://ide.codersports.com',
    'http://127.0.0.1:8601',
    'http://localhost:8601'
]);
const UPSTREAM_REVISION = '493c8560716fa4470eeaac60cad76c217eea3cbc';
const MAX_PROJECT_JSON_BYTES = 12 * 1024 * 1024;
const MAX_SB3_BYTES = 48 * 1024 * 1024;

const utf8Bytes = value => new TextEncoder().encode(value).byteLength;

const resolveParentOrigin = () => {
    if (!document.referrer) return null;
    try {
        const origin = new URL(document.referrer).origin;
        return ALLOWED_PARENT_ORIGINS.has(origin) ? origin : null;
    } catch (_error) {
        return null;
    }
};

export const installCoderSportsController = ({state}) => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('controller') !== '1') return () => {};
    const sessionId = params.get('session');
    const parentOrigin = resolveParentOrigin();
    if (!sessionId || !parentOrigin || window.parent === window) return () => {};

    let disposed = false;
    const vm = state.store.getState().scratchGui.vm;
    const send = (payload, transfer = []) => {
        if (disposed) return;
        window.parent.postMessage({protocol: PROTOCOL, sessionId, ...payload}, parentOrigin, transfer);
    };
    const sendError = (requestId, code, detail = '') => send({type: 'scratch-error', requestId, code, detail});
    const serialize = () => {
        const projectJson = vm.toJSON();
        if (utf8Bytes(projectJson) > MAX_PROJECT_JSON_BYTES) throw new Error('SCRATCH_PROJECT_JSON_TOO_LARGE');
        return projectJson;
    };

    const handleMessage = async event => {
        if (disposed || event.source !== window.parent || event.origin !== parentOrigin) return;
        const message = event.data;
        if (!message || typeof message !== 'object' || message.protocol !== PROTOCOL || message.sessionId !== sessionId) return;
        const requestId = message.requestId;
        try {
            switch (message.type) {
            case 'scratch-load-project': {
                if (typeof message.projectJson !== 'string' || utf8Bytes(message.projectJson) > MAX_PROJECT_JSON_BYTES) {
                    throw new Error('SCRATCH_PROJECT_JSON_INVALID');
                }
                JSON.parse(message.projectJson);
                await vm.loadProject(message.projectJson);
                send({type: 'scratch-project-state', requestId, projectJson: serialize()});
                break;
            }
            case 'scratch-request-project-state':
                send({type: 'scratch-project-state', requestId, projectJson: serialize()});
                break;
            case 'scratch-load-sb3': {
                if (!(message.sb3 instanceof ArrayBuffer) || message.sb3.byteLength > MAX_SB3_BYTES) {
                    throw new Error('SCRATCH_SB3_INVALID');
                }
                await vm.loadProject(message.sb3);
                send({type: 'scratch-project-state', requestId, projectJson: serialize()});
                break;
            }
            case 'scratch-request-sb3': {
                const blob = await vm.saveProjectSb3();
                const sb3 = await blob.arrayBuffer();
                if (sb3.byteLength > MAX_SB3_BYTES) throw new Error('SCRATCH_SB3_TOO_LARGE');
                send({type: 'scratch-sb3', requestId, sb3}, [sb3]);
                break;
            }
            case 'scratch-set-readonly':
                if (message.readOnly === true) sendError(requestId, 'SCRATCH_READONLY_NOT_PROVEN');
                break;
            case 'scratch-dispose':
                disposed = true;
                window.removeEventListener('message', handleMessage);
                vm.off('PROJECT_CHANGED', handleProjectChanged);
                vm.runtime.off('PROJECT_LOADED', handleProjectLoaded);
                break;
            default:
                sendError(requestId, 'SCRATCH_PARENT_MESSAGE_TYPE_REJECTED', String(message.type || ''));
            }
        } catch (error) {
            sendError(requestId, error?.message || 'SCRATCH_CHILD_ERROR');
        }
    };

    const handleProjectChanged = () => send({type: 'scratch-project-changed'});
    let readySent = false;
    const sendReady = () => {
        if (readySent || disposed) return;
        readySent = true;
        send({
            type: 'scratch-ready',
            upstream: {repository: 'scratchfoundation/scratch-editor', revision: UPSTREAM_REVISION, version: '15.1.1'}
        });
    };
    const handleProjectLoaded = () => sendReady();

    window.addEventListener('message', handleMessage);
    vm.on('PROJECT_CHANGED', handleProjectChanged);
    vm.runtime.on('PROJECT_LOADED', handleProjectLoaded);
    if (Array.isArray(vm.runtime.targets) && vm.runtime.targets.length > 0) sendReady();

    return () => {
        disposed = true;
        window.removeEventListener('message', handleMessage);
        vm.off('PROJECT_CHANGED', handleProjectChanged);
        vm.runtime.off('PROJECT_LOADED', handleProjectLoaded);
    };
};
